import queryClient from '@src/app/providers/queryClient';
import {
  ALERT_SUMMARY_TOPIC,
  type AlertSummaryChangedEvent,
  publishAlertRealtimeEvent,
} from '@src/features/alerts/model';
import settings from '@src/shared/config';
import { useTopicRefresh } from '@src/shared/realtime';
import type { TopicMessage } from '@src/shared/websocket';
import { useCallback } from 'react';

function parseAlertSummaryEvent(
  message: TopicMessage,
): AlertSummaryChangedEvent | null {
  // The shared socket already routed by topic and parsed the frame, so only
  // the payload shape still needs checking.
  if (typeof message.reason !== 'string') return null;

  const alertId =
    typeof message.alert_id === 'number' ? message.alert_id : undefined;
  return {
    reason: message.reason,
    ...(alertId === undefined ? {} : { alertId }),
  };
}

export default function AlertSummarySocketController() {
  const refreshSummary = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['root', 'alerts', 'summary'],
    });
  }, []);
  const handleMessage = useCallback((message: TopicMessage) => {
    const event = parseAlertSummaryEvent(message);
    if (!event) return;
    if (settings.DEBUG) {
      console.info('Alerts summary changed', {
        reason: event.reason,
        alertId: event.alertId,
      });
    }
    publishAlertRealtimeEvent(event);
  }, []);

  useTopicRefresh(ALERT_SUMMARY_TOPIC, handleMessage, refreshSummary);

  return null;
}
